import { useState, useEffect, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, ArrowRight, Users, Award, Calendar, Zap } from 'lucide-react';

const CounterAnimation = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
};

const TypingText = () => {
  const words = ["Innovation", "Excellence", "Technology", "Future", "Research"];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = words[currentWordIndex];
    const timer = setTimeout(
      () => {
        if (isDeleting) {
          setDisplayedText((prev) => prev.slice(0, -1));
          if (displayedText === "") {
            setIsDeleting(false);
            setCurrentWordIndex((prev) => (prev + 1) % words.length);
          }
        } else {
          setDisplayedText((prev) => currentWord.slice(0, prev.length + 1));
          if (displayedText === currentWord) {
            setTimeout(() => setIsDeleting(true), 2000);
          }
        }
      },
      isDeleting ? 50 : 150,
    );

    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentWordIndex, words]);

  return (
    <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
      {displayedText}
      <span className="animate-pulse">|</span>
    </span>
  );
};

const VideoHeroSection = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);

    // Check authentication status
    const token = localStorage.getItem("jwtToken");
    setIsLoggedIn(!!token);

    // Auto-play video when component mounts
    if (videoRef.current) {
      const video = videoRef.current;

      const handleLoadedData = () => {
        setIsVideoLoaded(true);
        setVideoError(false);
      };

      const handleError = () => {
        setVideoError(true);
        setIsVideoLoaded(false);
      };

      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('error', handleError);
      video.addEventListener('canplay', handleLoadedData);

      // Try to play the video
      video.play().catch((error) => {
        console.warn('Video autoplay failed:', error);
        setVideoError(true);
      });

      return () => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
        video.removeEventListener('canplay', handleLoadedData);
      };
    }
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const stats = [
    { icon: Users, label: "Active Members", value: 150, suffix: "+" },
    { icon: Award, label: "Projects", value: 45, suffix: "" },
    { icon: Calendar, label: "Events", value: 80, suffix: "+" },
  ];

  return (
    <div className="relative h-screen w-full overflow-hidden bg-slate-950">
      {/* Background Video */}
      <video
        ref={videoRef}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${isVideoLoaded && !videoError ? 'opacity-100' : 'opacity-0'
          }`}
        autoPlay
        muted={isMuted}
        loop
        playsInline
        preload="metadata"
        poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23020617'/%3E%3Cstop offset='50%25' style='stop-color:%231e293b'/%3E%3Cstop offset='100%25' style='stop-color:%23020617'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23bg)'/%3E%3C/svg%3E"
      >

        <source src="https://res.cloudinary.com/dzisiobww/video/upload/v1761753438/19096561-uhd_3840_2160_24fps_d6xti3.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Fallback Background for when video doesn't load */}
      <div className={`absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 transition-opacity duration-1000 ${isVideoLoaded && !videoError ? 'opacity-0' : 'opacity-100'
        }`} />



      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Video Controls */}
      <div className={`absolute top-20 right-4 sm:right-6 z-30 flex gap-2 sm:gap-3 transition-all duration-500 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
        }`}>
        <button
          onClick={togglePlay}
          className="group flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-black/40 backdrop-blur-md border border-white/20 shadow-lg transition-all duration-200 hover:bg-black/60 hover:scale-110 hover:border-white/40"
        >
          {isPlaying ? (
            <Pause className="h-4 w-4 sm:h-5 sm:w-5 text-white transition-transform group-hover:scale-110" />
          ) : (
            <Play className="h-4 w-4 sm:h-5 sm:w-5 text-white ml-0.5 transition-transform group-hover:scale-110" />
          )}
        </button>
        <button
          onClick={toggleMute}
          className="group flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-black/40 backdrop-blur-md border border-white/20 shadow-lg transition-all duration-200 hover:bg-black/60 hover:scale-110 hover:border-white/40"
        >
          {isMuted ? (
            <VolumeX className="h-4 w-4 sm:h-5 sm:w-5 text-white transition-transform group-hover:scale-110" />
          ) : (
            <Volume2 className="h-4 w-4 sm:h-5 sm:w-5 text-white transition-transform group-hover:scale-110" />
          )}
        </button>
      </div>

      {/* Main Content */}
     <div className="relative z-10 flex h-full items-center justify-center px-4 mt-32 sm:mt-0 pt-20 sm:pt-16">


        <div className="mx-auto max-w-6xl text-center">
          {/* Badge */}
          <div className={`mb-8 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-sm font-medium text-blue-300 backdrop-blur-sm">
              <Zap className="h-4 w-4" />
              Aeronaut Drone Society
            </div>
          </div>

          {/* Main Heading */}
          <div className={`mb-8 transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h1 className="mb-6 text-5xl font-bold leading-tight text-white md:text-7xl lg:text-8xl">
              Pioneering
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent">
                Aerial Innovation
              </span>
            </h1>
            <p className="mx-auto max-w-3xl text-xl text-gray-300 md:text-2xl">
              Advancing the future of drone technology through cutting-edge research,
              collaborative innovation, and excellence in <TypingText />
            </p>
          </div>

          {/* CTA Buttons */}
          <div className={`mb-16 flex flex-col items-center justify-center gap-4 sm:flex-row transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            {!isLoggedIn && (
              <button
                onClick={() => (window.location.href = "/signup")}
                className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-2xl transition-all hover:scale-105 hover:shadow-blue-500/25"
              >
                Join Our Mission
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
            )}

            <button
              onClick={() =>
                window.open("https://youtube.com/shorts/x5JfGKDCWk4?si=kwtgsnTs-8z6Myd7", "_blank")
              }
              className="flex items-center gap-2 rounded-full border-2 border-white/30 bg-white/10 px-8 py-4 text-lg font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/20 hover:scale-105 active:scale-95"
            >
              <Play className="h-5 w-5" />
              Watch Our Story
            </button>
          </div>

          {/* Statistics */}
          <div className={`grid grid-cols-1 gap-8 sm:grid-cols-3 transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            {stats.map((stat, index) => (
              <div
                key={index}
                className="group rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-all hover:bg-white/10"
              >
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-gradient-to-r from-blue-500 to-purple-500 p-3">
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-white">
                  <CounterAnimation end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
     <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 mt-10">
  <div className="flex flex-col items-center gap-2 text-white/60">
    <span className="text-sm mt-5">Scroll to explore</span>
    <div className="h-8 w-0.5 animate-pulse bg-white/40" />
  </div>
</div>


      {/* Gradient Overlay at Bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-950 to-transparent" />
    </div>
  );
};

export default VideoHeroSection;